'use client'

/* eslint-disable @next/next/no-img-element */

import type { ReactNode } from 'react'
import { useState } from 'react'
import {
  landingpage_skills_3d_chart_copy_click,
  landingpage_skills_3d_chart_download_click,
  landingpage_skills_data_report_copy_click,
  landingpage_skills_data_report_download_click,
  landingpage_skills_multi_chart_copy_click,
  landingpage_skills_multi_chart_download_click,
  landingpage_skills_sankey_chart_copy_click,
  landingpage_skills_sankey_chart_download_click
} from '@/constants/trace'
import { cn, trackEvent } from '@/lib/utils'
import { Check } from 'lucide-react'
import { includedSkillItems, type IncludedSkillItem } from './skill-data'
import { copySkillText } from './skill-copy'

const SKILL_CARD_TRACE: Record<string, { copy: string; download: string }> = {
  生成多个图表: {
    copy: landingpage_skills_multi_chart_copy_click,
    download: landingpage_skills_multi_chart_download_click
  },
  生成数据汇报: {
    copy: landingpage_skills_data_report_copy_click,
    download: landingpage_skills_data_report_download_click
  },
  生成桑基图: {
    copy: landingpage_skills_sankey_chart_copy_click,
    download: landingpage_skills_sankey_chart_download_click
  },
  生成3D图表: {
    copy: landingpage_skills_3d_chart_copy_click,
    download: landingpage_skills_3d_chart_download_click
  }
}

// 新版设计这里已经不是轮播，而是 4 张交错左右的静态列表卡片。
// 复制按钮的反馈仍然保留为局部交互，所以整个 section 继续保持在 client。
export function SkillIncludedSection() {
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  const handleCopy = async (text: string, key: string) => {
    const trace = SKILL_CARD_TRACE[key]
    if (trace) trackEvent(trace.copy)
    const success = await copySkillText(text)

    if (!success) {
      setCopiedKey(null)
      return
    }

    setCopiedKey(key)
    window.setTimeout(() => {
      setCopiedKey(current => (current === key ? null : current))
    }, 1600)
  }

  return (
    <section className="mx-auto w-full max-w-[1200px] px-4 md:px-6 xl:px-0">
      <div className="flex justify-center">
        <h2 className="w-full max-w-[995px] text-center text-[34px] font-bold leading-[1.3] tracking-[-0.03em] text-[#353745] md:text-[48px] xl:text-[64px] xl:leading-[85px]">
          从图表生成到汇报表达，开箱即用
        </h2>
      </div>

      <div className="mt-10 flex flex-col gap-6 md:mt-12 md:gap-8 xl:mt-16 xl:gap-10">
        {includedSkillItems.map((item, index) => (
          <SkillListCard
            key={item.name}
            item={item}
            reversed={index % 2 === 1}
            copied={copiedKey === item.name}
            onCopy={() => handleCopy(item.prompt, item.name)}
            trace={SKILL_CARD_TRACE[item.name]}
          />
        ))}
      </div>
    </section>
  )
}

type SkillListCardProps = {
  item: IncludedSkillItem
  reversed: boolean
  copied: boolean
  onCopy: () => void
  trace?: { copy: string; download: string }
}

function SkillListCard({ item, reversed, copied, onCopy, trace }: SkillListCardProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-8 rounded-[16px] bg-[#F3F6FF] px-6 py-6 md:px-8 md:py-8 xl:min-h-[490px] xl:items-center xl:gap-20 xl:px-20 xl:py-20',
        reversed ? 'xl:flex-row-reverse' : 'xl:flex-row'
      )}
    >
      <div className="flex w-full justify-center xl:w-auto xl:shrink-0 xl:justify-start">
        {/* 预览图在小一档屏幕直接改成满宽容器，避免继续沿用桌面定宽把卡片撑爆。 */}
        <div className="flex h-[220px] w-full max-w-[465px] items-center justify-center overflow-hidden rounded-[12px] bg-[linear-gradient(180deg,#685EF6_8.32%,#D7E0FD_113.85%)] md:h-[280px] xl:h-[330px] xl:w-[465px]">
          <img src={item.previewImage} alt={`${item.title}预览图`} className="h-full w-full object-cover" />
        </div>
      </div>

      <div className="flex w-full flex-1 items-center justify-center">
        <div className="flex w-full flex-col items-center justify-center gap-8 text-center md:gap-10 xl:items-start xl:gap-[50px] xl:text-left">
          <div className="flex flex-col gap-3 md:gap-4">
            <div className="text-[28px] font-semibold leading-[1.2] text-[#353745] md:text-[32px] xl:text-[40px] xl:leading-[32px]">{item.title}</div>
            <p className="mx-auto max-w-[495px] text-[16px] leading-[1.7] text-[#555974] md:text-[20px] md:leading-[1.6] xl:mx-0 xl:text-[24px] xl:leading-[32px]">
              {item.description}
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <SkillCopyButton onClick={onCopy} copied={copied}>
              复制安装提示词
            </SkillCopyButton>

            <div className="flex flex-wrap items-center justify-center gap-2 text-[14px] leading-[1.6] text-[#898FA7] md:text-[16px] md:leading-[20px] xl:justify-start">
              <DownloadIcon />
              {item.downloadUrl ? (
                <>
                  <span>想手动安装？</span>
                  <a
                    href={item.downloadUrl}
                    download
                    className="transition duration-200 hover:text-[#685EF6]"
                    onClick={() => {
                      if (trace) trackEvent(trace.download)
                    }}
                  >
                    点击下载安装包
                  </a>
                </>
              ) : (
                <span>想手动安装？暂未上架</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function SkillCopyButton({ children, onClick, copied }: { children: ReactNode; onClick: () => void; copied: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex min-h-[56px] w-full items-center justify-center gap-2 rounded-[50px] bg-[#685EF6] px-4 text-[16px] leading-none text-white transition duration-200 hover:-translate-y-0.5 sm:min-h-[60px] sm:w-[284px] sm:text-[18px] xl:h-[68px] xl:text-[24px]"
    >
      {copied ? <CopySuccessIcon /> : <CopyIcon />}
      <span>{copied ? '已复制提示词' : children}</span>
    </button>
  )
}

function CopyIcon() {
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" aria-hidden="true" className="h-[30px] w-[30px] shrink-0">
      <path
        d="M7.5 8.99375V5.81812C7.5 4.75022 8.287 3.88452 9.25781 3.88452H24.4922C25.463 3.88452 26.25 4.75022 26.25 5.81812V22.5759C26.25 23.6438 25.463 24.5095 24.4922 24.5095H21.5727"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.043 9.50952H5.33203C4.4583 9.50952 3.75 10.2965 3.75 11.2673V26.5017C3.75 27.4725 4.4583 28.2595 5.33203 28.2595H19.043C19.9167 28.2595 20.625 27.4725 20.625 26.5017V11.2673C20.625 10.2965 19.9167 9.50952 19.043 9.50952Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CopySuccessIcon() {
  return <Check aria-hidden="true" className="h-[30px] w-[30px] shrink-0" strokeWidth={2.4} />
}

function DownloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 2.5V9.16667" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M5.33334 6.83331L8.00001 9.49998L10.6667 6.83331" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 12.8333H13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}
